/** @jsxImportSource @emotion/react */
import { Box, useTheme, Typography, Chip, Card, CardContent } from '@mui/material'
import { Visibility, VisibilityOff, Book, ListAlt } from '@mui/icons-material'
import { Story } from '@app/components/Risk/types'
import { previewPlaceholderStyles } from './StoryBuilder.styles'
import { addCloudfrontUrl, defaultRiskStoryCoverImage } from '../Stories.utils'

interface StoryPreviewProps {
  story: Partial<Story>
  selectedChapterIndex?: number
  selectedStepIndex?: number
}

export const StoryPreview = ({
  story,
  selectedChapterIndex,
  selectedStepIndex,
}: StoryPreviewProps) => {
  const theme = useTheme()

  const totalSteps = story.chapters?.reduce((acc, chapter) => acc + chapter.steps.length, 0) || 0

  if (!story.title && !story.description && (!story.chapters || story.chapters.length === 0)) {
    return (
      <Box css={previewPlaceholderStyles(theme)}>
        <Book />
        <Typography variant="h6">Story Preview</Typography>
        <Typography variant="body2" color="text.secondary">
          Start adding content to see a preview of your story
        </Typography>
      </Box>
    )
  }

  const selectedChapter =
    selectedChapterIndex !== undefined ? story.chapters?.[selectedChapterIndex] : undefined
  const _selectedStep =
    selectedChapter && selectedStepIndex !== undefined
      ? selectedChapter.steps[selectedStepIndex]
      : undefined

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'auto',
        bgcolor: theme.palette.grey[100],
        p: 3,
      }}
    >
      {/* Story Header Card */}
      <Card sx={{ mb: 3 }}>
        <Box
          sx={{
            height: 200,
            backgroundImage: `url(${addCloudfrontUrl(story.coverImageUrl || defaultRiskStoryCoverImage)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(transparent 50%, rgba(0,0,0,0.7) 100%)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              left: 16,
              right: 16,
              color: 'white',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              {story.title || 'Untitled Story'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                size="small"
                icon={story.visibility === 'Public' ? <Visibility /> : <VisibilityOff />}
                label={story.visibility || 'Private'}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Chip
                size="small"
                icon={<Book />}
                label={`${story.chapters?.length || 0} chapters`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              <Chip
                size="small"
                icon={<ListAlt />}
                label={`${totalSteps} steps`}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
            </Box>
          </Box>
        </Box>
        <CardContent>
          <Typography variant="body1" color="text.secondary">
            {story.description || 'No description provided'}
          </Typography>
          {story.summary && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {story.summary}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Branding Preview */}
      {story.branding && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Branding Colours
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: story.branding.primary,
                  border: `1px solid ${theme.palette.divider}`,
                }}
                title={`Primary: ${story.branding.primary}`}
              />
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  bgcolor: story.branding.secondary,
                  border: `1px solid ${theme.palette.divider}`,
                }}
                title={`Secondary: ${story.branding.secondary}`}
              />
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Chapters & Steps Preview */}
      {story.chapters && story.chapters.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Story Structure
            </Typography>
            {story.chapters.map((chapter, chapterIdx) => (
              <Box
                key={chapterIdx}
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor:
                    selectedChapterIndex === chapterIdx
                      ? theme.palette.primary.main + '10'
                      : theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${
                    selectedChapterIndex === chapterIdx
                      ? theme.palette.primary.main
                      : theme.palette.divider
                  }`,
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Chapter {chapterIdx + 1}: {chapter.title || 'Untitled'}
                </Typography>
                {chapter.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {chapter.description}
                  </Typography>
                )}
                <Box sx={{ pl: 2, mt: 1 }}>
                  {chapter.steps.map((step, stepIdx) => (
                    <Box
                      key={stepIdx}
                      sx={{
                        py: 0.5,
                        px: 1,
                        mb: 0.5,
                        bgcolor:
                          selectedChapterIndex === chapterIdx && selectedStepIndex === stepIdx
                            ? theme.palette.secondary.main + '10'
                            : 'transparent',
                        borderRadius: 0.5,
                        border:
                          selectedChapterIndex === chapterIdx && selectedStepIndex === stepIdx
                            ? `1px solid ${theme.palette.secondary.main}`
                            : '1px solid transparent',
                      }}
                    >
                      <Typography variant="body2">
                        Step {stepIdx + 1}: {step.title || 'Untitled'}
                        {step.sections.length > 0 && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            ({step.sections.length} section{step.sections.length !== 1 ? 's' : ''})
                          </Typography>
                        )}
                      </Typography>
                      {step.location && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Location: {step.location.latitude.toFixed(4)},{' '}
                          {step.location.longitude.toFixed(4)}
                        </Typography>
                      )}
                      {(step.elements?.length ||
                        step.scenarios?.length ||
                        step.contexts?.length) && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Layers: {step.elements?.length || 0} assets, {step.scenarios?.length || 0}{' '}
                          hazards, {step.contexts?.length || 0} info
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </CardContent>
        </Card>
      )}
    </Box>
  )
}

export default StoryPreview
